import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getWeeklyShoppingList, toggleShoppingListItem } from '../api/dataService';
import { useLocale } from '../context/LocaleContext';

const ShoppingListDrawer = ({ isOpen, onClose, userCheckedItems = [] }) => {
    const [ingredients, setIngredients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [checkedItems, setCheckedItems] = useState(new Set(userCheckedItems));
    const [pendingItems, setPendingItems] = useState(new Set());
    const { t } = useLocale();

    useEffect(() => {
        if (isOpen && !loading) {
            fetchShoppingList();
        }
    }, [isOpen]);

    // Keep internal check state synced with user data if it updates externally
    useEffect(() => {
        setCheckedItems(new Set(userCheckedItems));
    }, [userCheckedItems]);

    const fetchShoppingList = async () => {
        try {
            setError(null);
            setLoading(true);
            const res = await getWeeklyShoppingList();
            if (res.data && res.data.success) {
                const nextIngredients = Array.isArray(res.data.ingredients) ? res.data.ingredients : [];
                const nextCheckedItems = Array.isArray(res.data.checkedItems) ? res.data.checkedItems : [];
                setIngredients(nextIngredients);
                setCheckedItems(new Set(nextCheckedItems));
            }
        } catch (err) {
            console.error("Error fetching shopping list:", err);
            setError(t('shoppingList.loadFailed', 'Could not load grocery list.'));
        } finally {
            setLoading(false);
        }
    };

    const handleToggleCheck = async (item) => {
        if (pendingItems.has(item)) {
            return;
        }

        const isChecked = checkedItems.has(item);
        const newCheckedState = !isChecked;

        // Optimistic UI update
        setPendingItems((prev) => {
            const next = new Set(prev);
            next.add(item);
            return next;
        });
        setCheckedItems((prev) => {
            const next = new Set(prev);
            if (newCheckedState) {
                next.add(item);
            } else {
                next.delete(item);
            }
            return next;
        });

        // API Call
        try {
            await toggleShoppingListItem(item, newCheckedState);
        } catch (err) {
            console.error("Failed to sync check state:", err);
            // Revert optimistic update on failure
            setCheckedItems((prev) => {
                const next = new Set(prev);
                if (newCheckedState) {
                    next.delete(item);
                } else {
                    next.add(item);
                }
                return next;
            });
        } finally {
            setPendingItems((prev) => {
                const next = new Set(prev);
                next.delete(item);
                return next;
            });
        }
    };

    // Prevent interactions behind the drawer when scrolling or tapping
    const handleBackdropClick = (e) => {
        if (e.target.id === 'drawer-backdrop') {
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div
                    id="drawer-backdrop"
                    onClick={handleBackdropClick}
                    className="fixed inset-0 z-50 flex flex-col justify-end bg-black/60 backdrop-blur-sm transition-opacity"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        // Prevent scroll events from bubbling up to the body
                        onClick={(e) => e.stopPropagation()}
                        className="bg-gray-900 border-t border-gray-800 rounded-t-3xl w-full max-w-md mx-auto h-[80vh] flex flex-col shadow-2xl relative overflow-hidden"
                    >
                        {/* Drag Handle Area (Visual only for now, drag-to-close could be added via Framer Motion's drag prop) */}
                        <div className="w-full h-8 flex items-center justify-center shrink-0 pt-2 pb-1" onClick={onClose}>
                            <div className="w-12 h-1.5 bg-gray-700 rounded-full"></div>
                        </div>

                        {/* Sticky Header */}
                        <div className="px-6 pb-4 border-b border-gray-800/60 flex justify-between items-center shrink-0">
                            <div>
                                <h2 className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
                                    {t('shoppingList.title', '7-Day Grocery List')}
                                </h2>
                                <p className="text-xs text-gray-400 mt-1">{t('shoppingList.subtitle', "Generated from this week's meals")}</p>
                            </div>
                            <button
                                onClick={onClose}
                                aria-label={t('shoppingList.close', 'Close shopping list')}
                                className="w-8 h-8 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-full text-gray-400 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 overscroll-contain pb-[env(safe-area-inset-bottom,20px)]">
                            {loading ? (
                                <div className="flex flex-col justify-center items-center h-full space-y-4">
                                    <div className="w-8 h-8 border-2 border-[var(--primary-color)] border-t-transparent rounded-full animate-spin"></div>
                                    <p className="text-sm text-gray-400">{t('shoppingList.loading', 'Extracting ingredients...')}</p>
                                </div>
                            ) : error ? (
                                <div className="flex justify-center items-center h-full text-red-400 text-sm">
                                    {error}
                                </div>
                            ) : ingredients.length === 0 ? (
                                <div className="flex flex-col justify-center items-center h-full text-gray-500 space-y-2">
                                    <p className="text-sm">{t('shoppingList.empty', 'No upcoming meal quests found for this week.')}</p>
                                </div>
                            ) : (
                                <ul className="space-y-3">
                                    {ingredients.map((item) => {
                                        const isChecked = checkedItems.has(item);
                                        const isPending = pendingItems.has(item);
                                        return (
                                            <li key={item}>
                                                <button
                                                    onClick={() => handleToggleCheck(item)}
                                                    aria-label={t('shoppingList.toggleItem', 'Toggle {item}', { item })}
                                                    disabled={isPending}
                                                    className={`w-full text-left p-4 rounded-xl border transition-all duration-200 flex items-center gap-4 ${isChecked
                                                        ? 'bg-gray-800/40 border-gray-800 opacity-60'
                                                        : 'bg-gray-800/80 border-gray-700 hover:bg-gray-700'
                                                        }`}
                                                >
                                                    <div className={`w-6 h-6 rounded-md flex items-center justify-center border transition-colors shrink-0 ${isChecked
                                                        ? 'bg-[var(--primary-color)] border-[var(--primary-color)]'
                                                        : 'bg-gray-900 border-gray-600'
                                                        }`}>
                                                        {isChecked && (
                                                            <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className={`text-sm font-medium leading-tight select-none transition-all ${isChecked ? 'text-gray-500 line-through' : 'text-gray-200'
                                                        }`}>
                                                        {item}
                                                    </span>
                                                    {isPending && (
                                                        <span className="ml-auto text-xs text-gray-500">{t('shoppingList.syncing', 'Syncing...')}</span>
                                                    )}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ShoppingListDrawer;
